import { HttpParams } from '@angular/common/http';
import { MOCK_BRANCHES } from 'mocks/shared/prototype-clients.const';
import {
	CreatePromptVersionAdminDTO,
	CreatePromptVersionDTO,
	ProductCustomPromptDetail,
	ProductCustomPromptSummary,
} from 'services/product-custom-prompt/product-custom-prompt.service';
import {
	CreateSetupTemplateDTO,
	SchemaItem,
	SetupTemplate,
	SetupTemplateDetail,
	TemplateHistoricEntry,
	TemplateNamedRef,
	TemplateStatus,
	TemplateVersionSummary,
	UpdateSetupTemplateDTO,
} from 'services/setup-template/setup-template.model';
import { AuditUser } from 'services/user/user.model';
import { MockHttpError, MockRoute, mockRoute, newMockId, nowIso, paginate } from '../mock-api';
import { buildCurrentUser } from '../session/session.mock';
import {
	MOCK_CATALOG,
	MOCK_PEOPLE,
	MOCK_VERTICAL_INFO,
	productSetupIdOf,
} from '../shared/mock-context.const';
import {
	CADASTRO_CREATE_PAGE_CATALOG_ID,
	CADASTRO_PROMPT_MASTER,
	CADASTRO_PROMPT_SEEDS,
	CADASTRO_TEMPLATE_SEEDS,
} from './cadastro.const';

// ── Helpers ──────────────────────────────────────────────────────────────────

function currentAuditUser(): AuditUser {
	try {
		const user = buildCurrentUser();
		return { id: user.id, name: user.name, email: user.email, image: user.image };
	} catch {
		return MOCK_PEOPLE.admin;
	}
}

function currentProductSetupId(): string {
	try {
		return buildCurrentUser().productSetupId || productSetupIdOf('trabalhista', 'cadastro');
	} catch {
		return productSetupIdOf('trabalhista', 'cadastro');
	}
}

function matchesSearch(search: string | null, ...values: (string | null | undefined)[]): boolean {
	if (!search) return true;
	const term = search.toLowerCase();
	return values.some((value) => value?.toLowerCase().includes(term));
}

function sortDirection(query: HttpParams): 1 | -1 {
	return query.get('sortOrder') === 'asc' ? 1 : -1;
}

// ── Prompts versionados do product setup ─────────────────────────────────────

const promptsBySetup = new Map<string, ProductCustomPromptDetail[]>();

function seedPrompts(productSetupId: string): ProductCustomPromptDetail[] {
	return CADASTRO_PROMPT_SEEDS.map((seed) => ({
		id: `pcp-${productSetupId}-v${seed.version}`,
		productSetupId,
		version: seed.version,
		description: seed.description,
		promptUser: seed.promptUser,
		promptMaster: CADASTRO_PROMPT_MASTER,
		schema: seed.schema,
		isActive: seed.isActive,
		linkedJobsCount: seed.linkedJobsCount,
		createdAt: seed.createdAt,
		createdBy: seed.createdBy,
		updatedAt: seed.createdAt,
		updatedBy: seed.createdBy,
		deletedAt: null,
		deletedBy: null,
	}));
}

/** Prompts do setup — semeados sob demanda para qualquer product setup do Cadastro */
function promptsOf(productSetupId: string): ProductCustomPromptDetail[] {
	let prompts = promptsBySetup.get(productSetupId);
	if (!prompts) {
		prompts = seedPrompts(productSetupId);
		promptsBySetup.set(productSetupId, prompts);
	}
	return prompts;
}

function findPrompt(id: string): ProductCustomPromptDetail {
	for (const prompts of promptsBySetup.values()) {
		const prompt = prompts.find((candidate) => candidate.id === id);
		if (prompt) return prompt;
	}
	// Ids semeados carregam o setup: garante o detalhe mesmo antes de listar
	const seededSetup = /^pcp-(.+)-v\d+$/.exec(id)?.[1];
	const seeded = seededSetup
		? promptsOf(seededSetup).find((candidate) => candidate.id === id)
		: undefined;
	if (seeded) return seeded;
	throw new MockHttpError(404, 'Prompt não encontrado', `Nenhuma versão de prompt com id ${id}.`);
}

function toPromptSummary(prompt: ProductCustomPromptDetail): ProductCustomPromptSummary {
	return {
		id: prompt.id,
		productSetupId: prompt.productSetupId,
		version: prompt.version,
		description: prompt.description,
		promptUser: prompt.promptUser,
		schema: prompt.schema,
		isActive: prompt.isActive,
		createdAt: prompt.createdAt,
		createdBy: prompt.createdBy,
	};
}

function toPromptPublicDetail(prompt: ProductCustomPromptDetail) {
	const { promptMaster: _promptMaster, ...publicDetail } = prompt;
	return publicDetail;
}

function createPromptVersion(
	productSetupId: string,
	dto: CreatePromptVersionDTO,
	promptMaster?: string,
) {
	if (!dto.description?.trim()) {
		throw new MockHttpError(400, 'Dados inválidos', 'A descrição da versão é obrigatória.');
	}
	const prompts = promptsOf(productSetupId);
	const activate = dto.activateImmediately ?? false;
	if (activate) prompts.forEach((prompt) => (prompt.isActive = false));

	const author = currentAuditUser();
	const createdAt = nowIso();
	const version = Math.max(0, ...prompts.map((prompt) => prompt.version)) + 1;
	const created: ProductCustomPromptDetail = {
		id: newMockId('pcp'),
		productSetupId,
		version,
		description: dto.description,
		promptUser: dto.promptUser,
		promptMaster:
			promptMaster ??
			prompts.find((prompt) => prompt.isActive)?.promptMaster ??
			CADASTRO_PROMPT_MASTER,
		schema: dto.schema,
		isActive: activate,
		linkedJobsCount: 0,
		createdAt,
		createdBy: author,
		updatedAt: createdAt,
		updatedBy: author,
		deletedAt: null,
		deletedBy: null,
	};
	prompts.push(created);
	return created;
}

const PROMPT_ROUTES: MockRoute[] = [
	mockRoute('GET', '/product-custom-prompt', ({ query }) => {
		const productSetupId = query.get('productSetupId') ?? currentProductSetupId();
		const search = query.get('search');
		const direction = sortDirection(query);
		const items = promptsOf(productSetupId)
			.filter((prompt) =>
				matchesSearch(search, prompt.description, prompt.promptUser, `v${prompt.version}`),
			)
			.sort((first, second) => (first.version - second.version) * direction)
			.map(toPromptSummary);
		return paginate(items, query);
	}),
	mockRoute('GET', '/product-custom-prompt/:id/admin', ({ params }) => findPrompt(params['id'])),
	mockRoute('GET', '/product-custom-prompt/:id', ({ params }) =>
		toPromptPublicDetail(findPrompt(params['id'])),
	),
	mockRoute<CreatePromptVersionDTO>('POST', '/product-custom-prompt', ({ body }) =>
		createPromptVersion(currentProductSetupId(), body),
	),
	mockRoute<CreatePromptVersionAdminDTO>(
		'POST',
		'/product-setup/:productSetupId/custom-prompt',
		({ params, body }) => createPromptVersion(params['productSetupId'], body, body.promptMaster),
	),
];

// ── Templates de configuração (admin) ────────────────────────────────────────

interface TemplateGroup {
	id: string;
	verticalId: string;
	branchId: string | null;
}

interface TemplateVersion {
	id: string;
	groupId: string;
	version: number;
	status: TemplateStatus;
	description: string | null;
	promptMaster: string;
	promptUser: string;
	schema: SchemaItem[];
	publishedAt: string | null;
	archivedAt: string | null;
	createdAt: string;
	createdBy: AuditUser | null;
	updatedAt: string;
	updatedBy: AuditUser | null;
}

const CADASTRO_CATALOG_REF: TemplateNamedRef = {
	id: MOCK_CATALOG.cadastro.id,
	name: MOCK_CATALOG.cadastro.name,
};
const CADASTRO_CATALOG_IDS = new Set([MOCK_CATALOG.cadastro.id, CADASTRO_CREATE_PAGE_CATALOG_ID]);

const templateGroups: TemplateGroup[] = CADASTRO_TEMPLATE_SEEDS.map((seed) => ({
	id: seed.id,
	verticalId: MOCK_VERTICAL_INFO[seed.verticalSlug].id,
	branchId: seed.branchId,
}));

const templateVersions: TemplateVersion[] = CADASTRO_TEMPLATE_SEEDS.flatMap((seed) =>
	seed.versions.map((version) => ({
		id: `${seed.id}-v${version.version}`,
		groupId: seed.id,
		version: version.version,
		status: version.status,
		description: version.description,
		promptMaster: CADASTRO_PROMPT_MASTER,
		promptUser: version.promptUser,
		schema: version.schema,
		publishedAt: version.publishedAt,
		archivedAt: version.archivedAt,
		createdAt: version.createdAt,
		createdBy: version.createdBy,
		updatedAt: version.updatedAt,
		updatedBy: version.createdBy,
	})),
);

function verticalRef(verticalId: string): TemplateNamedRef {
	const vertical = Object.values(MOCK_VERTICAL_INFO).find((info) => info.id === verticalId);
	return { id: verticalId, name: vertical?.name ?? verticalId };
}

function branchRef(branchId: string | null): TemplateNamedRef | null {
	if (!branchId) return null;
	const branch = MOCK_BRANCHES.find((candidate) => candidate.id === branchId);
	return { id: branchId, name: branch?.name ?? branchId };
}

function templateKeyOf(group: TemplateGroup): string {
	return ['cadastro', group.verticalId, group.branchId ?? 'global'].join(':');
}

function findGroup(groupId: string): TemplateGroup {
	const group = templateGroups.find((candidate) => candidate.id === groupId);
	if (!group) throw new MockHttpError(404, 'Template não encontrado');
	return group;
}

function versionsOf(groupId: string): TemplateVersion[] {
	return templateVersions
		.filter((version) => version.groupId === groupId)
		.sort((first, second) => second.version - first.version);
}

function findVersion(id: string): TemplateVersion {
	// A listagem cai no id do agrupamento quando não há versão — resolve para a mais relevante
	const groupVersions = versionsOf(id);
	const version =
		templateVersions.find((candidate) => candidate.id === id) ??
		groupVersions.find((candidate) => candidate.status === TemplateStatus.PUBLISHED) ??
		groupVersions[0];
	if (!version)
		throw new MockHttpError(
			404,
			'Template não encontrado',
			`Nenhuma versão de template com id ${id}.`,
		);
	return version;
}

function toVersionSummary(version: TemplateVersion | undefined): TemplateVersionSummary | null {
	if (!version) return null;
	return {
		id: version.id,
		version: version.version,
		status: version.status,
		description: version.description,
		updatedAt: version.updatedAt,
	};
}

function toTemplateRow(group: TemplateGroup): SetupTemplate {
	const versions = versionsOf(group.id);
	const byStatus = (status: TemplateStatus) =>
		versions.find((version) => version.status === status);
	return {
		id: group.id,
		templateKey: templateKeyOf(group),
		productCatalog: CADASTRO_CATALOG_REF,
		vertical: verticalRef(group.verticalId),
		branch: branchRef(group.branchId),
		published: toVersionSummary(byStatus(TemplateStatus.PUBLISHED)),
		inDrafting: toVersionSummary(byStatus(TemplateStatus.DRAFT)),
		inTesting: toVersionSummary(byStatus(TemplateStatus.TESTING)),
		inArchived: toVersionSummary(byStatus(TemplateStatus.ARCHIVED)),
		updatedAt:
			versions
				.map((version) => version.updatedAt)
				.sort()
				.at(-1) ?? nowIso(),
	};
}

function toHistoricEntry(version: TemplateVersion): TemplateHistoricEntry {
	return {
		id: version.id,
		version: version.version,
		status: version.status,
		description: version.description,
		createdAt: version.createdAt,
		createdBy: version.createdBy,
		updatedAt: version.updatedAt,
	};
}

function toTemplateDetail(version: TemplateVersion): SetupTemplateDetail {
	const group = findGroup(version.groupId);
	return {
		id: version.id,
		productCatalogId: CADASTRO_CATALOG_REF.id,
		verticalId: group.verticalId,
		branchId: group.branchId,
		description: version.description,
		promptMaster: version.promptMaster,
		promptUser: version.promptUser,
		schema: version.schema,
		version: version.version,
		status: version.status,
		publishedAt: version.publishedAt,
		archivedAt: version.archivedAt,
		createdAt: version.createdAt,
		createdBy: version.createdBy,
		updatedAt: version.updatedAt,
		updatedBy: version.updatedBy,
		deletedAt: null,
		deletedBy: null,
		productCatalog: CADASTRO_CATALOG_REF,
		vertical: verticalRef(group.verticalId),
		branch: branchRef(group.branchId),
		historic: versionsOf(group.id).map(toHistoricEntry),
	};
}

function touch(version: TemplateVersion) {
	version.updatedAt = nowIso();
	version.updatedBy = currentAuditUser();
}

function promoteVersion(version: TemplateVersion): TemplateVersion {
	const siblings = versionsOf(version.groupId).filter((sibling) => sibling.id !== version.id);
	if (version.status === TemplateStatus.DRAFT) {
		// Só um template em teste por vez: o anterior volta para o arquivo
		siblings
			.filter((sibling) => sibling.status === TemplateStatus.TESTING)
			.forEach((sibling) => {
				sibling.status = TemplateStatus.ARCHIVED;
				sibling.archivedAt = nowIso();
			});
		version.status = TemplateStatus.TESTING;
	} else if (version.status === TemplateStatus.TESTING) {
		siblings
			.filter((sibling) => sibling.status === TemplateStatus.PUBLISHED)
			.forEach((sibling) => {
				sibling.status = TemplateStatus.ARCHIVED;
				sibling.archivedAt = nowIso();
			});
		version.status = TemplateStatus.PUBLISHED;
		version.publishedAt = nowIso();
	} else {
		throw new MockHttpError(
			409,
			'Transição inválida',
			'Apenas rascunhos e templates em teste podem ser promovidos.',
		);
	}
	touch(version);
	return version;
}

function demoteVersion(version: TemplateVersion): TemplateVersion {
	if (version.status !== TemplateStatus.TESTING) {
		throw new MockHttpError(
			409,
			'Transição inválida',
			'Apenas templates em teste podem voltar para rascunho.',
		);
	}
	const hasDraft = versionsOf(version.groupId).some(
		(sibling) => sibling.status === TemplateStatus.DRAFT,
	);
	if (hasDraft) {
		throw new MockHttpError(409, 'Rascunho existente', 'Já existe um rascunho para este template.');
	}
	version.status = TemplateStatus.DRAFT;
	touch(version);
	return version;
}

function cloneVersion(source: TemplateVersion): TemplateVersion {
	const existingDraft = versionsOf(source.groupId).find(
		(sibling) => sibling.status === TemplateStatus.DRAFT,
	);
	if (existingDraft) return existingDraft;

	const author = currentAuditUser();
	const createdAt = nowIso();
	const draft: TemplateVersion = {
		...source,
		id: newMockId('tpl-ver'),
		version: Math.max(...versionsOf(source.groupId).map((sibling) => sibling.version)) + 1,
		status: TemplateStatus.DRAFT,
		schema: structuredClone(source.schema),
		publishedAt: null,
		archivedAt: null,
		createdAt,
		createdBy: author,
		updatedAt: createdAt,
		updatedBy: author,
	};
	templateVersions.push(draft);
	return draft;
}

function createTemplate(dto: CreateSetupTemplateDTO): TemplateVersion {
	const branchId = dto.branchId ?? null;
	const duplicated = templateGroups.some(
		(group) => group.verticalId === dto.verticalId && group.branchId === branchId,
	);
	if (duplicated) {
		throw new MockHttpError(
			409,
			'Template já existe',
			'Já existe um template do Cadastro para esta vertical e segmento. Crie uma nova versão a partir dele.',
		);
	}
	const group: TemplateGroup = { id: newMockId('tpl'), verticalId: dto.verticalId, branchId };
	templateGroups.unshift(group);

	const author = currentAuditUser();
	const createdAt = nowIso();
	const version: TemplateVersion = {
		id: `${group.id}-v1`,
		groupId: group.id,
		version: 1,
		status: TemplateStatus.DRAFT,
		description: dto.description ?? null,
		promptMaster: dto.promptMaster,
		promptUser: dto.promptUser,
		schema: dto.schema ?? [],
		publishedAt: null,
		archivedAt: null,
		createdAt,
		createdBy: author,
		updatedAt: createdAt,
		updatedBy: author,
	};
	templateVersions.push(version);
	return version;
}

function updateTemplate(version: TemplateVersion, dto: UpdateSetupTemplateDTO): TemplateVersion {
	if (version.status !== TemplateStatus.DRAFT) {
		throw new MockHttpError(409, 'Edição não permitida', 'Apenas rascunhos podem ser editados.');
	}
	if (dto.description !== undefined) version.description = dto.description;
	if (dto.promptMaster !== undefined) version.promptMaster = dto.promptMaster;
	if (dto.promptUser !== undefined) version.promptUser = dto.promptUser;
	if (dto.schema !== undefined) version.schema = dto.schema;
	touch(version);
	return version;
}

function deleteTemplate(version: TemplateVersion) {
	if (version.status !== TemplateStatus.DRAFT) {
		throw new MockHttpError(
			409,
			'Exclusão não permitida',
			'Apenas rascunhos podem ser descartados.',
		);
	}
	templateVersions.splice(templateVersions.indexOf(version), 1);
	if (versionsOf(version.groupId).length === 0) {
		templateGroups.splice(templateGroups.indexOf(findGroup(version.groupId)), 1);
	}
}

function listTemplates(query: HttpParams) {
	const productCatalogId = query.get('productCatalogId');
	if (productCatalogId && !CADASTRO_CATALOG_IDS.has(productCatalogId)) return paginate([], query);

	const verticalId = query.get('verticalId');
	const branchId = query.get('branchId');
	const search = query.get('search');
	const rows = templateGroups
		.filter((group) => !verticalId || group.verticalId === verticalId)
		.filter((group) => !branchId || group.branchId === branchId)
		.map(toTemplateRow)
		.filter((row) =>
			matchesSearch(
				search,
				row.templateKey,
				row.vertical.name,
				row.branch?.name,
				row.published?.description,
				row.inTesting?.description,
				row.inDrafting?.description,
			),
		)
		.sort((first, second) => second.updatedAt.localeCompare(first.updatedAt));
	return paginate(rows, query);
}

const TEMPLATE_ROUTES: MockRoute[] = [
	mockRoute('GET', '/product-prompt-template/templates', ({ query }) => listTemplates(query)),
	mockRoute<CreateSetupTemplateDTO>('POST', '/product-prompt-template', ({ body }) =>
		toTemplateDetail(createTemplate(body)),
	),
	mockRoute('POST', '/product-prompt-template/:id/promote', ({ params }) =>
		toTemplateDetail(promoteVersion(findVersion(params['id']))),
	),
	mockRoute('POST', '/product-prompt-template/:id/demote', ({ params }) =>
		toTemplateDetail(demoteVersion(findVersion(params['id']))),
	),
	mockRoute('POST', '/product-prompt-template/:id/clone', ({ params }) =>
		toTemplateDetail(cloneVersion(findVersion(params['id']))),
	),
	mockRoute('GET', '/product-prompt-template/:id', ({ params }) =>
		toTemplateDetail(findVersion(params['id'])),
	),
	mockRoute<UpdateSetupTemplateDTO>('PATCH', '/product-prompt-template/:id', ({ params, body }) =>
		toTemplateDetail(updateTemplate(findVersion(params['id']), body)),
	),
	mockRoute('DELETE', '/product-prompt-template/:id', ({ params }) => {
		deleteTemplate(findVersion(params['id']));
		return null;
	}),
];

export const CADASTRO_MOCK_ROUTES: MockRoute[] = [...PROMPT_ROUTES, ...TEMPLATE_ROUTES];
